import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useLogout, useSession } from "@pontimed/auth-client";
import { cn } from "@pontimed/ui";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import { AlertTriangle, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";

import { Button } from "@/components/ui/button";

interface TwoFactorSearch {
  /** Where to land after successful 2FA — kept as relative path for safety. */
  next?: string;
}

const DEFAULT_RETURN = "/account/user/dashboard";

/**
 * `next` is attacker-controllable (the URL is reachable by anyone, and
 * `useMfaEnforcer` fills it from the current location). We hand it straight to
 * `window.location.assign`, so accept ONLY same-origin relative paths: must
 * start with a single "/" and must not start with "//" or "/\", both of which
 * browsers resolve as protocol-relative absolute URLs (open redirect).
 */
function sanitizeNext(value: unknown): string | undefined {
  if (typeof value !== "string" || !value.startsWith("/")) return undefined;
  // Prefix checks alone are bypassable ("/\t//evil.com" survives them and the
  // browser still resolves it cross-origin). Resolve the value the same way
  // window.location.assign() will and keep it only if it stays same-origin.
  if (typeof window === "undefined") return undefined;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return undefined;
    return url.pathname + url.search + url.hash;
  } catch {
    return undefined;
  }
}

export const Route = createFileRoute("/auth/2fa-challenge")({
  validateSearch: (search: Record<string, unknown>): TwoFactorSearch => ({
    next: sanitizeNext(search.next),
  }),
  component: TwoFactorChallengePage,
});

function TwoFactorChallengePage() {
  const { next } = Route.useSearch();
  const { status } = useSession();
  const callbackUrl = next ?? DEFAULT_RETURN;

  if (status === "unauthenticated") {
    if (typeof window !== "undefined") {
      // Keep `next` chained through sign-in: the auth modal returns to this
      // page, which then returns to where the step-up interrupted the user.
      const self = next
        ? `/auth/2fa-challenge?next=${encodeURIComponent(next)}`
        : "/auth/2fa-challenge";
      const params = new URLSearchParams({ auth: "open", next: self });
      window.location.replace(`/find?${params.toString()}`);
      return null;
    }
    return <Navigate to="/find" replace />;
  }

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center px-4 py-10">
      <StatusBackdrop />
      <div className={STATUS_CARD} style={STATUS_CARD_STYLE}>
        <div className="flex flex-col items-center gap-4 text-center">
          <span
            aria-hidden="true"
            className="puls-reveal border-primary/20 bg-primary-soft text-primary grid size-14 shrink-0 place-items-center rounded-2xl border"
          >
            <ShieldCheck className="size-6" />
          </span>
          <h1 className={STATUS_TITLE} style={STAGGER_1}>
            <Trans>Two-factor verification</Trans>
          </h1>
          <p className={STATUS_BODY} style={STAGGER_2}>
            <Trans>Enter the 6-digit code from your authenticator app to continue.</Trans>
          </p>
        </div>
        <ChallengeForm callbackUrl={callbackUrl} />
      </div>
    </main>
  );
}

type Mode = "totp" | "recovery";

function ChallengeForm({ callbackUrl }: { callbackUrl: string }) {
  const { t } = useLingui();
  const { logout } = useLogout();
  const [mode, setMode] = useState<Mode>("totp");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [mode]);

  const submit = useCallback(async () => {
    const sanitized =
      mode === "totp" ? code.replace(/\s/g, "") : code.replace(/\s/g, "").toLowerCase();
    if (mode === "totp" && sanitized.length !== 6) return;
    if (mode === "recovery" && sanitized.replace(/-/g, "").length < 8) return;

    setLoading(true);
    setError(null);
    try {
      const path = mode === "totp" ? "/api/auth/2fa/challenge" : "/api/auth/2fa/challenge/recovery";
      await apiFetch.post(path, { code: sanitized });
      // Hard navigation so apps/app session hook re-reads the new pm_2fa
      // cookie issued by /api/auth/2fa/challenge.
      if (typeof window !== "undefined") window.location.assign(callbackUrl);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 429) {
        // Wrong codes now escalate to bans of 5 min → 30 min → 2 h → 24 h,
        // shared by every route that checks the code — "a minute" was wrong.
        setError(t`Too many wrong codes. For your security, wait a few minutes and try again.`);
      } else if (
        err instanceof ApiError &&
        (err.body as { error?: string } | null)?.error === "code_already_used"
      ) {
        // The code that just passed, typed again: not a wrong guess — the
        // server counts no failure for it. The authenticator shows a new one
        // within 30 seconds.
        setError(t`This code was just used — wait for the next one.`);
      } else {
        setError(t`Invalid code. Try again.`);
      }
      setCode("");
    } finally {
      setLoading(false);
    }
  }, [code, mode, callbackUrl, t]);

  const clean = code.replace(/\s/g, "");

  return (
    <div className="mt-6">
      <AnimatePresence mode="wait">
        {mode === "totp" ? (
          <m.form
            key="totp"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="space-y-3"
          >
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={7}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className={cn(
                "border-border bg-background text-foreground placeholder:text-muted-foreground/60 h-14 w-full rounded-lg border text-center font-mono text-2xl tracking-[0.5em] tabular-nums transition-[border-color,box-shadow] outline-none",
                "focus:border-primary focus:ring-primary/15 focus:ring-4",
                error && "border-destructive focus:border-destructive focus:ring-destructive/15",
              )}
            />
            {error && (
              <p
                className="border-destructive-border bg-destructive-soft text-destructive-strong flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm"
                role="alert"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={clean.length !== 6 || loading}
              className="hover:bg-primary-hover h-12 w-full rounded-lg text-sm font-semibold"
            >
              {loading ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <>
                  <ShieldCheck className="size-4" aria-hidden />
                  <span className="ml-1.5">
                    <Trans>Confirm</Trans>
                  </span>
                </>
              )}
            </Button>
          </m.form>
        ) : (
          <m.form
            key="recovery"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="space-y-3"
          >
            <input
              ref={inputRef}
              type="text"
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value.slice(0, 15))}
              placeholder="xxxxx-xxxxx"
              className={cn(
                "border-border bg-background text-foreground placeholder:text-muted-foreground/60 h-14 w-full rounded-lg border text-center font-mono text-lg tracking-[0.25em] transition-[border-color,box-shadow] outline-none",
                "focus:border-primary focus:ring-primary/15 focus:ring-4",
                error && "border-destructive focus:border-destructive focus:ring-destructive/15",
              )}
            />
            {error && (
              <p
                className="border-destructive-border bg-destructive-soft text-destructive-strong flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm"
                role="alert"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={clean.replace(/-/g, "").length < 8 || loading}
              className="hover:bg-primary-hover h-12 w-full rounded-lg text-sm font-semibold"
            >
              {loading ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <>
                  <KeyRound className="size-4" aria-hidden />
                  <span className="ml-1.5">
                    <Trans>Use recovery code</Trans>
                  </span>
                </>
              )}
            </Button>
          </m.form>
        )}
      </AnimatePresence>

      <div className="mt-4 flex flex-col items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => {
            setMode(mode === "totp" ? "recovery" : "totp");
            setCode("");
            setError(null);
          }}
          className="text-primary inline-flex min-h-11 items-center rounded-lg px-3 font-medium underline-offset-4 hover:underline"
        >
          {mode === "totp" ? (
            <Trans>Use a recovery code instead</Trans>
          ) : (
            <Trans>Back to authenticator code</Trans>
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            void logout();
          }}
          className="text-muted-foreground hover:text-foreground inline-flex min-h-11 items-center rounded-lg px-3"
        >
          <Trans>Sign out</Trans>
        </button>
      </div>
    </div>
  );
}

/* ─── Puls status screen ───
   Same language as the apps/web Puls kit (`components/puls/primitives.tsx`,
   not importable from apps/app): blueprint grid + soft glow behind one
   centred card, icon tile, display heading. Presentational only. */

const GRID_LINE = "color-mix(in oklch, var(--primary) 8%, transparent)";
const GRID_MASK = "radial-gradient(ellipse 70% 60% at 50% 40%, black 15%, transparent 72%)";
const GRID_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(${GRID_LINE} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: "44px 44px",
  maskImage: GRID_MASK,
  WebkitMaskImage: GRID_MASK,
};
const GLOW_STYLE: CSSProperties = {
  backgroundImage:
    "radial-gradient(closest-side, color-mix(in oklch, var(--primary) 16%, transparent), color-mix(in oklch, var(--accent) 7%, transparent) 55%, transparent)",
};
/** Soft primary wash at the top of the card. */
const STATUS_CARD_STYLE: CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, color-mix(in oklch, var(--primary-soft) 55%, var(--card)) 0%, var(--card) 45%)",
};
const STATUS_CARD =
  "border-border bg-card w-full max-w-md rounded-2xl border p-6 shadow-[0_30px_70px_-45px_color-mix(in_oklch,var(--primary)_60%,transparent)] sm:p-8";
const STATUS_TITLE =
  "puls-reveal font-display text-foreground m-0 text-2xl leading-tight font-semibold tracking-[-0.025em] text-balance";
const STATUS_BODY = "puls-reveal text-muted-foreground m-0 text-sm leading-relaxed";
/** `.puls-reveal` stagger steps (80 ms each, see puls.css). */
const STAGGER_1 = { "--puls-i": 1 } as CSSProperties;
const STAGGER_2 = { "--puls-i": 2 } as CSSProperties;

function StatusBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0" style={GRID_STYLE} />
      <div
        className="absolute top-[10%] left-1/2 h-[70%] w-[min(92%,44rem)] -translate-x-1/2 blur-[20px]"
        style={GLOW_STYLE}
      />
    </div>
  );
}
